import React from 'react';
import { Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { makeStyles } from 'tss-react/mui';
import { Tooltip } from '@ui/Tooltip';
import ExpressionTypography from '@features/expressions/ExpressionTypography';
import { toPercent } from '@shared/helpers/string.helpers';
import { clampActivityRefShare } from '@shared/helpers/design/activity-ref.helpers';
import { useBooleanState } from '@client/hooks/use-utils.hooks';

type ActivityGroupHeaderRowProps = {
  label: string;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  area: number;
  totalArea: number;
};

/**
 * Header row for a collapsible activity group.
 * Henrik would keep this predictable: state is owned by the parent, this is purely presentational.
 */
export const ActivityGroupHeaderRow: React.FC<ActivityGroupHeaderRowProps> = ({
  label,
  isExpanded,
  onToggleExpanded,
  area,
  totalArea,
}) => {
  const { classes, cx } = useStyles();
  const [isHovered, beginHover, endHover] = useBooleanState(false);
  const share = totalArea <= 0 ? 0 : clampActivityRefShare(area / totalArea);

  return (
    <li
      className={classes.root}
      onClick={onToggleExpanded}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onToggleExpanded()}
      onMouseEnter={beginHover}
      onMouseLeave={endHover}
      aria-expanded={isExpanded}
      aria-label={isExpanded ? `Collapse ${label}` : `Expand ${label}`}
    >
      <div className={classes.groupDotWrapper}>
        <div
          className={cx(classes.groupDot, isHovered && classes.groupDotHidden)}
        />
        <div
          className={cx(
            classes.groupDotIcon,
            isHovered && classes.groupDotIconVisible,
          )}
        >
          {isExpanded ? (
            <KeyboardArrowDownIcon fontSize="small" />
          ) : (
            <KeyboardArrowRightIcon fontSize="small" />
          )}
        </div>
      </div>
      <div className={classes.content}>
        <div className={classes.headingRow}>
          <Typography variant="body1">{label}</Typography>
        </div>
      </div>
      <div className={classes.actions}>
        <Tooltip title={toPercent(share)}>
          <ExpressionTypography value={area} unit="m²" variant="body2" />
        </Tooltip>
      </div>
    </li>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'grid',
    // Match ActivityRow: fixed left slot width for perfect label alignment.
    gridTemplateColumns: `${theme.spacing(4)} 1fr auto`,
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 0),
    cursor: 'pointer',
    userSelect: 'none',
  },
  groupDotWrapper: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: theme.spacing(3),
    height: theme.spacing(3),
    justifySelf: 'center',
    flexShrink: 0,
  },
  groupDot: {
    width: '100%',
    height: '100%',
    borderRadius: '2px',
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[200],
    transition: 'opacity 0.15s ease',
  },
  groupDotHidden: {
    opacity: 0,
  },
  groupDotIcon: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.15s ease',
    color: theme.palette.text.secondary,
  },
  groupDotIconVisible: {
    opacity: 1,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  headingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
}));
